import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  Search,
  Download,
  Eye,
  FileText,
  Calendar,
  User,
  Euro,
  Plus,
  Filter,
  Edit,
  Trash2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pagination } from "@/components/ui/pagination";

import { payrollAPI, employeesAPI } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";
import { exportPayslip } from "@/services/pdfExport.service";
import PayslipGenerator from "./PayslipGenerator";
import PayslipViewer from "./PayslipViewer";
import EditPayslipModal from "./EditPayslipModal";
import DeletePayslipModal from "./DeletePayslipModal";

export default function PayslipManagement() {
  const { toast, success: showSuccess, error: showError } = useToast();
  const { user } = useAuth();
  const [payslips, setPayslips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    employeeId: "",
    year: new Date().getFullYear(),
    month: "",
    status: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });

  // Modals
  const [showGenerator, setShowGenerator] = useState(false);
  const [showViewer, setShowViewer] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [payslipToEdit, setPayslipToEdit] = useState(null);
  const [payslipToDelete, setPayslipToDelete] = useState(null);

  const statusColors = {
    brouillon: "bg-gray-100 text-gray-800",
    valide: "bg-green-100 text-green-800",
    paye: "bg-blue-100 text-blue-800",
  };

  const statusLabels = {
    brouillon: "Brouillon",
    valide: "Validé",
    paye: "Payé",
  };

  useEffect(() => {
    loadPayslips();
    if (user?.role === "admin" || user?.role === "rh") {
      loadEmployees();
    }
  }, [filters, pagination.page]);

  // Add body class when modals open
  useEffect(() => {
    if (showGenerator || showViewer || showEditModal || showDeleteModal) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [showGenerator, showViewer, showEditModal, showDeleteModal]);

  const loadPayslips = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        ...filters,
      };

      // Convert employeeId to employe_id for API
      if (params.employeeId) {
        params.employe_id = params.employeeId;
        delete params.employeeId;
      }

      // Remove empty filters
      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          params[key] === undefined
        ) {
          delete params[key];
        }
      });

      const response = await payrollAPI.getPayslips(params);
      setPayslips(response.data.data || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta?.total || 0,
      }));
    } catch (error) {
      console.error("Error loading payslips:", error);
      showError("Erreur lors du chargement des bulletins de paie");
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await employeesAPI.getAll({ limit: 1000 });
      setEmployees(response.data?.data?.employees || []);
    } catch (error) {
      console.error("Error loading employees:", error);
      setEmployees([]); // Ensure employees is always an array
      showError("Erreur lors du chargement des employés");
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setTimeout(loadPayslips, 300);
  };

  const handleFilterChange = (key, value) => {
    // Convert "all" to empty string for API
    const apiValue = value === "all" ? "" : value;
    setFilters((prev) => ({ ...prev, [key]: apiValue }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleViewPayslip = (payslip) => {
    setSelectedPayslip(payslip);
    setShowViewer(true);
  };

  const handleEditPayslip = (payslip) => {
    setPayslipToEdit(payslip);
    setShowEditModal(true);
  };

  const handleDeletePayslip = (payslip) => {
    setPayslipToDelete(payslip);
    setShowDeleteModal(true);
  };

  const handlePayslipUpdated = (updatedPayslip) => {
    setPayslips((prev) =>
      prev.map((p) => (p.id === updatedPayslip.id ? updatedPayslip : p))
    );
    setShowEditModal(false);
    setPayslipToEdit(null);
  };

  const handlePayslipDeleted = (payslipId) => {
    setPayslips((prev) => prev.filter((p) => p.id !== payslipId));
    setShowDeleteModal(false);
    setPayslipToDelete(null);
    // Update pagination total
    setPagination((prev) => ({ ...prev, total: prev.total - 1 }));
  };

  const handleDownloadPayslip = async (payslip) => {
    try {
      await exportPayslip(payslip);
      showSuccess("Bulletin de paie téléchargé avec succès");
    } catch (error) {
      console.error("Error downloading payslip:", error);
      showError("Erreur lors du téléchargement du bulletin de paie");
    }
  };

  const handleExport = async () => {
    try {
      const params = { ...filters, search: searchTerm };
      // Convert employeeId to employeeId for export API (export API uses employeeId)
      if (params.employeeId) {
        // Keep as employeeId for export API
      }
      const response = await payrollAPI.exportPayslips(params);

      // Create blob and download link
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "bulletins_paie_export.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess("Données exportées avec succès");
    } catch (error) {
      console.error("Error exporting payslips:", error);
      showError("Erreur lors de l'export");
    }
  };

  const canManagePayroll = user?.role === "admin" || user?.role === "rh";

  const columns = [
    {
      accessorKey: "employe",
      header: "Employé",
      cell: ({ row }) => {
        const employe = row.original.employe;
        return (
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-sm font-medium text-blue-600">
                {employe?.prenom?.[0]}
                {employe?.nom?.[0]}
              </span>
            </div>
            <div>
              <p className="font-medium">
                {employe?.prenom} {employe?.nom}
              </p>
              <p className="text-sm text-gray-500">
                {employe?.departement?.nom}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "periode_debut",
      header: "Période",
      cell: ({ row }) => {
        const debut = new Date(row.original.periode_debut);
        const fin = new Date(row.original.periode_fin);
        return (
          <div className="text-sm">
            <p className="font-medium">
              {debut.toLocaleDateString("fr-MA", {
                month: "short",
                year: "numeric",
              })}
            </p>
            <p className="text-gray-500">
              {debut.getDate()}/{debut.getMonth() + 1} - {fin.getDate()}/
              {fin.getMonth() + 1}
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: "salaire_brut",
      header: "Salaire Brut",
      cell: ({ row }) => (
        <span className="font-medium text-green-600">
          {parseFloat(row.original.salaire_brut || 0).toLocaleString("fr-MA", {
            style: "currency",
            currency: "MAD",
          })}
        </span>
      ),
    },
    {
      accessorKey: "cotisations_sociales",
      header: "Cotisations",
      cell: ({ row }) => (
        <span className="font-medium text-red-600">
          -
          {parseFloat(row.original.cotisations_sociales || 0).toLocaleString(
            "fr-MA",
            {
              style: "currency",
              currency: "MAD",
            }
          )}
        </span>
      ),
    },
    {
      accessorKey: "frais_professionnels",
      header: "Frais Pro",
      cell: ({ row }) => (
        <span className="font-medium text-orange-600">
          -
          {parseFloat(row.original.frais_professionnels || 0).toLocaleString(
            "fr-MA",
            {
              style: "currency",
              currency: "MAD",
            }
          )}
        </span>
      ),
    },
    {
      accessorKey: "ir_net",
      header: "IR",
      cell: ({ row }) => (
        <span className="font-medium text-red-600">
          -
          {parseFloat(row.original.ir_net || 0).toLocaleString("fr-MA", {
            style: "currency",
            currency: "MAD",
          })}
        </span>
      ),
    },
    {
      accessorKey: "salaire_net",
      header: "Salaire Net",
      cell: ({ row }) => (
        <span className="font-bold text-green-700">
          {parseFloat(row.original.salaire_net || 0).toLocaleString("fr-MA", {
            style: "currency",
            currency: "MAD",
          })}
        </span>
      ),
    },
    {
      accessorKey: "statut",
      header: "Statut",
      cell: ({ row }) => {
        const statut = row.original.statut;
        const statutConfig = {
          brouillon: { label: "Brouillon", color: "bg-gray-100 text-gray-800" },
          valide: { label: "Validé", color: "bg-green-100 text-green-800" },
          paye: { label: "Payé", color: "bg-blue-100 text-blue-800" },
        };
        const config = statutConfig[statut] || statutConfig.brouillon;

        return <Badge className={config.color}>{config.label}</Badge>;
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleViewPayslip(row.original)}
            title="Voir les détails"
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDownloadPayslip(row.original)}
            title="Télécharger PDF"
          >
            <Download className="h-4 w-4" />
          </Button>
          {canManagePayroll && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEditPayslip(row.original)}
                title="Modifier"
                className="text-blue-600 hover:text-blue-700"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDeletePayslip(row.original)}
                title="Supprimer"
                className="text-red-600 hover:text-red-700"
                disabled={row.original.statut === "paye"}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FileText className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Bulletins
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {pagination.total}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Calendar className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Ce Mois</p>
                <p className="text-2xl font-bold text-gray-900">
                  {
                    payslips.filter((p) => {
                      const payslipDate = new Date(p.periode_debut);
                      const currentDate = new Date();
                      return (
                        payslipDate.getMonth() === currentDate.getMonth() &&
                        payslipDate.getFullYear() === currentDate.getFullYear()
                      );
                    }).length
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Euro className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Salaires
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {payslips
                    .reduce(
                      (sum, p) => sum + (parseFloat(p.salaire_net) || 0),
                      0
                    )
                    .toLocaleString("fr-MA", {
                      style: "currency",
                      currency: "MAD",
                    })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <User className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Employés</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(payslips.map((p) => p.employe_id)).size}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <CardTitle>Bulletins de Paie</CardTitle>
            <div className="flex flex-wrap gap-2">
              {canManagePayroll && (
                <>
                  <Button
                    onClick={() => setShowGenerator(true)}
                    className="flex items-center space-x-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Générer un bulletin</span>
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleExport}
                    className="flex items-center space-x-2"
                  >
                    <Download className="h-4 w-4" />
                    <span>Exporter</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Recherche
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {canManagePayroll && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Employé
                </label>
                <Select
                  value={filters.employeeId || "all"}
                  onValueChange={(value) =>
                    handleFilterChange("employeeId", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les employés" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les employés</SelectItem>
                    {Array.isArray(employees) &&
                      employees.map((employee) => (
                        <SelectItem key={employee.id} value={employee.id}>
                          {employee.prenom} {employee.nom}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Année</label>
              <Select
                value={filters.year.toString()}
                onValueChange={(value) =>
                  handleFilterChange("year", parseInt(value))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Toutes les années" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Mois</label>
              <Select
                value={filters.month || "all"}
                onValueChange={(value) => handleFilterChange("month", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tous les mois" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les mois</SelectItem>
                  {Array.from({ length: 12 }, (_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      {new Date(2024, i).toLocaleDateString("fr-FR", {
                        month: "long",
                      })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Statut
              </label>
              <Select
                value={filters.status || "all"}
                onValueChange={(value) => handleFilterChange("status", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tous les statuts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="brouillon">
                    {statusLabels.brouillon}
                  </SelectItem>
                  <SelectItem value="valide">{statusLabels.valide}</SelectItem>
                  <SelectItem value="paye">{statusLabels.paye}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  {canManagePayroll && <TableHead>Employé</TableHead>}
                  <TableHead>Période</TableHead>
                  <TableHead>Salaire Brut</TableHead>
                  <TableHead>Salaire Net</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Date de génération</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={canManagePayroll ? 7 : 6}
                      className="text-center py-8"
                    >
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="ml-2">Chargement...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : payslips.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={canManagePayroll ? 7 : 6}
                      className="text-center py-8"
                    >
                      <div className="text-gray-500">
                        <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p>Aucun bulletin de paie trouvé</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  payslips.map((payslip) => (
                    <TableRow key={payslip.id}>
                      {canManagePayroll && (
                        <TableCell>
                          <div>
                            <p className="font-medium">
                              {payslip.employe?.prenom} {payslip.employe?.nom}
                            </p>
                            <p className="text-sm text-gray-500">
                              {payslip.employe?.email}
                            </p>
                          </div>
                        </TableCell>
                      )}
                      <TableCell>
                        {new Date(payslip.periode_debut).toLocaleDateString(
                          "fr-FR"
                        )}{" "}
                        -{" "}
                        {new Date(payslip.periode_fin).toLocaleDateString(
                          "fr-FR"
                        )}
                      </TableCell>
                      <TableCell>
                        {parseFloat(payslip.salaire_brut || 0).toLocaleString(
                          "fr-MA",
                          {
                            style: "currency",
                            currency: "MAD",
                          }
                        )}
                      </TableCell>
                      <TableCell>
                        {parseFloat(payslip.salaire_net || 0).toLocaleString(
                          "fr-MA",
                          {
                            style: "currency",
                            currency: "MAD",
                          }
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[payslip.statut]}>
                          {statusLabels[payslip.statut]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {new Date(payslip.date_generation).toLocaleDateString(
                          "fr-FR"
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewPayslip(payslip)}
                            title="Voir les détails"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadPayslip(payslip)}
                            title="Télécharger PDF"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {canManagePayroll && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditPayslip(payslip)}
                                title="Modifier"
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeletePayslip(payslip)}
                                title="Supprimer"
                                className="text-red-600 hover:text-red-700"
                                disabled={payslip.statut === "paye"}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.total > pagination.limit && (
            <div className="mt-6 flex justify-center">
              <Pagination
                currentPage={pagination.page}
                totalPages={Math.ceil(pagination.total / pagination.limit)}
                onPageChange={(page) =>
                  setPagination((prev) => ({ ...prev, page }))
                }
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      {showGenerator && (
        <Dialog open={showGenerator} onOpenChange={setShowGenerator}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto z-[9999]">
            <DialogHeader>
              <DialogTitle>Générer un bulletin de paie</DialogTitle>
            </DialogHeader>
            <PayslipGenerator
              onClose={() => setShowGenerator(false)}
              onGenerated={(newPayslip) => {
                setPayslips((prev) => [newPayslip, ...prev]);
                setShowGenerator(false);
                showSuccess("Bulletin de paie généré avec succès");
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {showViewer && selectedPayslip && (
        <PayslipViewer
          payslip={selectedPayslip}
          onClose={() => setShowViewer(false)}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && payslipToEdit && (
        <EditPayslipModal
          payslip={payslipToEdit}
          onClose={() => {
            setShowEditModal(false);
            setPayslipToEdit(null);
          }}
          onUpdated={handlePayslipUpdated}
        />
      )}

      {/* Delete Modal */}
      {showDeleteModal && payslipToDelete && (
        <DeletePayslipModal
          payslip={payslipToDelete}
          onClose={() => {
            setShowDeleteModal(false);
            setPayslipToDelete(null);
          }}
          onDeleted={handlePayslipDeleted}
        />
      )}
    </div>
  );
}
